// React and core imports
import { ReactElement, useCallback, useMemo, Suspense, lazy, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// External libraries
import { useQueryClient } from '@tanstack/react-query';
import { Col, Row, Divider, Spin, notification, Form, Input } from 'antd';
import Skeleton from 'antd/lib/skeleton';
import { browserPopupRedirectResolver, OAuthProvider, signInWithPopup } from '@firebase/auth';

// Internal modules
import Button from 'app/atoms/Button';
import { FORGOT_PASSWORD, ROOT, SIGNUP } from 'configs/routeNames';
import { preloadAssets, validateMessages } from './constants';
import { saveTokenToLocal } from './services';
import { SignInPayload } from './types';
import { auth } from './firebaseConfig';
import { NotificationProps } from 'utils/types';
import { useAuthentication } from './hooks/useAuthentication';

// Assets
import loginBannerUrl from './../../../assets/login-banner.webp?url';

const Logo = lazy(() => import('./../../../assets/itinerehub-logo-signin.svg?react'));
const MicrosoftLogo = lazy(() => import('./../../../assets/microsoft-logo.svg?react'));

// Loading fallback for logos
const LogoFallback = () => <div className="w-[200px] h-[50px] bg-gray-200 animate-pulse rounded m-auto mb-10" />;

const MicrosoftLogoFallback = () => <div className="w-5 h-5 bg-gray-200 animate-pulse rounded" />;

const Login = (): ReactElement => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const queryClient = useQueryClient();

  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  const from = location.state?.from?.pathname;
  const search = location.state?.from?.search || '';

  // Preload critical assets on component mount
  useEffect(() => {
    preloadAssets(loginBannerUrl);
    return () => {
      // Cleanup preload links on unmount
      document.querySelectorAll('link[rel="preload"], link[rel="preconnect"]').forEach((link) => link.remove());
    };
  }, []);

  // Memoized navigation callbacks
  const forgotPassword = useCallback(() => navigate(`${ROOT}${FORGOT_PASSWORD}`), [navigate]);
  const signUp = useCallback(() => navigate(`${ROOT}${SIGNUP}`), [navigate]);

  // Use the reusable authentication hook
  const { signIn: signInApi, startSession: startSessionApi } = useAuthentication({
    setIsAuthenticating,
  });

  const onFinish = useCallback((values: SignInPayload) => signInApi.mutate(values), [signInApi]);

  const handleMicrosoftLogin = useCallback(async () => {
    try {
      setIsAuthenticating(true);
      const provider = new OAuthProvider('microsoft.com');
      provider.setCustomParameters({
        tenant: import.meta.env.VITE_REACT_APP_MICROSOFT_TENANT_ID,
        display: 'popup',
        prompt: 'select_account',
      });
      const result = await signInWithPopup(auth, provider, browserPopupRedirectResolver);
      if (result.user) {
        saveTokenToLocal({
          accessToken: await result.user.getIdToken(),
        });

        await startSessionApi.mutateAsync(true); // Microsoft SSO login
        queryClient.invalidateQueries({ queryKey: ['auth', 'user-info'] });

        // Navigate directly to the target route without defaulting to ROOT first
        navigate(from ? from + search : ROOT, { replace: true });
      }
    } catch (error) {
      if (error.code === 'auth/popup-closed-by-user') {
        setIsAuthenticating(false);
        return;
      }

      if (error.code === 'auth/account-exists-with-different-credential') {
        notification.error({
          message: t('auth.microsoftSSO.error.account_exists_with_different_credential'),
          ...NotificationProps,
        });
      } else {
        await auth.signOut();
        localStorage.clear();
      }
    } finally {
      setIsAuthenticating(false);
    }
  }, [navigate, queryClient, startSessionApi, t, location]);

  // Memoized form items to prevent re-renders
  const formItems = useMemo(
    () => (
      <>
        <Form.Item
          label={t('form.email.label')}
          name="email"
          className="label-before-content-none"
          rules={[
            { required: true, message: t('form.email.required') },
            { type: 'email', message: t('auth.signup.rules.email_invalid') },
          ]}
        >
          <Input placeholder={t('form.email.placeholder')} type="email" size="large" data-testid="email-field" />
        </Form.Item>
        <div className="relative">
          <Form.Item
            label={t('form.password.label')}
            name="password"
            className="label-before-content-none"
            rules={[{ required: true, message: t('form.password.required') }]}
          >
            <Input.Password
              placeholder={t('form.password.placeholder')}
              size="large"
              type="password"
              data-testid="password-field"
              maxLength={16}
            />
          </Form.Item>
          <div className="relative bottom-[25px] right-0 text-right rtl:text-left">
            <Button className="px-0" type="link" htmlType="button" onClick={forgotPassword}>
              {t('forgot_password')}
            </Button>
          </div>
        </div>
        <Form.Item className="mb-4">
          <Button
            data-testid="submit-button"
            type="primary"
            htmlType="submit"
            block
            loading={signInApi.isPending}
            size="large"
          >
            {t('submit')}
          </Button>
        </Form.Item>
      </>
    ),
    [t, forgotPassword, signInApi.isPending],
  );

  // Memoize background style with proper type safety
  const backgroundImgStyle = useMemo(
    () => ({
      backgroundImage: `url(${loginBannerUrl})`,
      backgroundColor: '#f5f5f5', // Fallback color while loading
    }),
    [],
  );

  // Add loading overlay if authenticating
  if (isAuthenticating) {
    return (
      <div
        className="fixed inset-0 flex items-center justify-center bg-white bg-opacity-75 z-50"
        data-testid="loading-overlay"
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <main className="app-container">
      <Row className="py-8 px-10">
        <Col xs={24} xl={12} className="hidden xl:block">
          {!isImageLoaded && (
            <div className="login-background">
              <Skeleton
                active
                paragraph={false}
                title={{
                  width: '100%',
                  className: 'login-background !h-[calc(100vh-64px)] mt-0 !rounded-2xl transition-opacity duration-300',
                }}
                data-testid="login-banner-skeleton"
              />
            </div>
          )}
          <img
            src={loginBannerUrl}
            alt="Login background"
            className="hidden"
            onLoad={() => setIsImageLoaded(true)}
            loading="eager"
            decoding="sync"
          />
          <div
            style={backgroundImgStyle}
            className={`login-background absolute inset-0 bg-no-repeat bg-cover bg-center transition-opacity duration-300 ${
              isImageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
          />
        </Col>
        <Col xs={24} xl={12} className="flex items-center justify-center">
          <div className="max-w-[440px] w-full login-background py-16">
            <Suspense fallback={<LogoFallback />}>
              <Logo className="m-auto mb-10" width="200" height="50" />
            </Suspense>
            <h2 className="h3-bold mb-2.5 text-center text-primary-600">{t('auth.login.heading')}</h2>
            <p className="body-regular text-center mb-10">{t('auth.login.subheading')}</p>
            <Form
              layout="vertical"
              name="basic"
              initialValues={{ remember: true }}
              onFinish={onFinish}
              validateMessages={validateMessages}
            >
              {formItems}
            </Form>
            <div className="flex flex-col gap-2">
              <p className="text-center">
                {t('auth.login.new_here')} {''}
                <Button className="px-0 underline" type="link" htmlType="button" onClick={signUp}>
                  {t('signup')}
                </Button>
              </p>
              <Divider plain>
                <span className="text-xs font-semibold text-neutral-700">{t('auth.login.or')}</span>
              </Divider>
              <Button
                icon={
                  <Suspense fallback={<MicrosoftLogoFallback />}>
                    <MicrosoftLogo />
                  </Suspense>
                }
                data-testid="sso-button"
                type="default"
                block
                size="large"
                onClick={handleMicrosoftLogin}
              >
                {t('sso_button')}
              </Button>
            </div>
          </div>
        </Col>
      </Row>
    </main>
  );
};

export default Login;
